import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useMemo } from 'react';
import {
  FetchManifestQuery,
  type ManifestStage,
} from '@/lib/api/system/ingestion-observability';

export interface ResolvedGroup {
  key: string;
  displayName: string;
  order: number;
  stages: ManifestStage[];
}

export function useIngestionManifest(opts?: { showInternal?: boolean }) {
  const tenantId = useAuthStore((s) => s.tenant?.tenantId ?? '');
  const roles = useAuthStore((s) => s.roles);
  const isSuperAdmin = roles.includes('SuperAdmin');
  const showInternal = opts?.showInternal ?? isSuperAdmin;

  const { data: manifest, isLoading, error } = useQuery(
    FetchManifestQuery(tenantId)
  );

  const visibleStages = useMemo(() => {
    if (!manifest) return [];
    return manifest.stages.filter((s) => {
      if (s.visibility === 'public') return true;
      if (s.visibility === 'internal' || s.visibility === 'erroronly') return showInternal;
      return false;
    });
  }, [manifest, showInternal]);

  const groups = useMemo((): ResolvedGroup[] => {
    if (!manifest) return [];
    return manifest.groups
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((g) => ({
        ...g,
        stages: visibleStages
          .filter((s) => s.group === g.key)
          .sort((a, b) => a.order - b.order),
      }));
  }, [manifest, visibleStages]);

  const stageToGroup = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of visibleStages) {
      if (s.group) map.set(s.key, s.group);
    }
    return map;
  }, [visibleStages]);

  const stageDisplayNames = useMemo(() => {
    if (!manifest) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const s of manifest.stages) {
      map.set(s.key, s.displayName);
    }
    return map;
  }, [manifest]);

  const stageVisibility = useMemo(() => {
    if (!manifest) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const s of manifest.stages) {
      map.set(s.key, s.visibility);
    }
    return map;
  }, [manifest]);

  const stageOptions = useMemo(() => {
    return visibleStages
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        value: s.key,
        label: s.displayName,
      }));
  }, [visibleStages]);

  const outcomeOptions = useMemo(() => {
    if (!manifest) return [];
    return manifest.outcomes.map((o) => ({
      value: o.key,
      label: o.displayName,
    }));
  }, [manifest]);

  const getTypesForStage = useMemo(() => {
    const typeMap = new Map<string, string[]>();
    if (manifest) {
      for (const s of manifest.stages) {
        typeMap.set(s.key, s.knownTypes);
      }
    }
    return (stageKey: string): string[] => typeMap.get(stageKey) ?? [];
  }, [manifest]);

  const allTypeOptions = useMemo(() => {
    if (!manifest) return [];
    const seen = new Set<string>();
    for (const s of visibleStages) {
      for (const t of s.knownTypes) {
        seen.add(t);
      }
    }
    return Array.from(seen)
      .sort()
      .map((t) => ({ value: t, label: t }));
  }, [manifest, visibleStages]);

  const getGroupForStage = useMemo(() => {
    return (stageKey: string): string => stageToGroup.get(stageKey) ?? 'unknown';
  }, [stageToGroup]);

  const stageOrder = useMemo(() => {
    if (!manifest) return new Map<string, number>();
    const map = new Map<string, number>();
    for (const s of manifest.stages) {
      map.set(s.key, s.order);
    }
    return map;
  }, [manifest]);

  return {
    manifest,
    isLoading,
    error,
    groups,
    stageToGroup,
    stageDisplayNames,
    stageVisibility,
    stageOptions,
    outcomeOptions,
    allTypeOptions,
    getTypesForStage,
    getGroupForStage,
    stageOrder,
    visibleStages,
    isSuperAdmin,
  };
}
